import { keyBy, max } from "lodash";
import Folder from "../../models/Folder";

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createId } from "@paralleldrive/cuid2";
import { immer } from "zustand/middleware/immer";
import produce from "immer";
import { captureException } from "@sentry/react";
import Project from "../../models/Project";
import Auth from "../../Auth";
import { createSafeStorage } from "../../stores/safeStorage";

function maxUpdatedAt(folders, projects) {
  if (!folders || !projects) return new Date(0);
  return max([...folders.map((folder) => folder.updated_at), ...projects.map((project) => project.updated_at)]);
}

function newLastActive() {
  // Add 2 seconds to the current time to compensate for the server time
  return new Date(Date.now() + 2000).toISOString();
}

export const useAllFoldersStore = create(
  persist(
    immer((set, get) => ({
      userId: null,
      foldersById: {},
      projectsById: {},

      optimisticUpdateFoldersById: {},
      optimisticUpdateProjectsById: {},

      _updatedAt() {
        const { foldersById, projectsById } = get();
        return maxUpdatedAt(Object.values(foldersById), Object.values(projectsById));
      },

      data(currentUserId) {
        const { userId, foldersById, optimisticUpdateFoldersById, projectsById, optimisticUpdateProjectsById } = get();

        if (userId !== currentUserId) {
          set({ userId: currentUserId, foldersById: {}, projectsById: {}, optimisticUpdateFoldersById: {}, optimisticUpdateProjectsById: {} });

          return {
            foldersById: {},
            projectsById: {},
          };
        }

        return {
          foldersById: Object.assign({}, foldersById, optimisticUpdateFoldersById),
          projectsById: Object.assign({}, projectsById, optimisticUpdateProjectsById),
        };
      },

      _getFolder(folderId) {
        const { foldersById, optimisticUpdateFoldersById } = get();
        return optimisticUpdateFoldersById[folderId] || foldersById[folderId] || undefined;
      },

      _getProject(projectId) {
        const { projectsById, optimisticUpdateProjectsById } = get();
        return optimisticUpdateProjectsById[projectId] || projectsById[projectId] || undefined;
      },

      getProjectFromFirebaseId(projectFirebaseId) {
        const { projectsById, optimisticUpdateProjectsById } = get();
        return (
          Object.values(optimisticUpdateProjectsById).find((project) => project.firebase_id === projectFirebaseId) ||
          Object.values(projectsById).find((project) => project.firebase_id === projectFirebaseId)
        );
      },

      isInTrash(projectId) {
        const { _getFolder, _getProject } = get();

        const project = _getProject(projectId);

        if (!project) {
          return false;
        }

        if (project.deleted) {
          return true;
        }

        let folderId = project.folder_id;
        while (folderId) {
          const folder = _getFolder(folderId);
          if (folder && folder.deleted) {
            return true;
          }

          folderId = folder?.parent_folder_id;
        }

        return false;
      },

      _pushOptimisticUpdateFolder(folderId, callback) {
        const version = createId();

        set((state) => {
          const folder = state._getFolder(folderId) ?? {};

          const newFolder = produce(folder, (draft) => {
            draft.id = folderId;
            draft.updated_at = new Date().toISOString();
            draft._version = version;

            callback(draft);
          });

          state.optimisticUpdateFoldersById[folderId] = newFolder;
        });

        return () => {
          set((state) => {
            if (state.optimisticUpdateFoldersById[folderId]?._version === version) {
              delete state.optimisticUpdateFoldersById[folderId];
            }
          });
        };
      },

      _pushOptimisticUpdateProject(projectId, callback) {
        const version = createId();

        set((state) => {
          const project = state._getProject(projectId) ?? {};

          const newProject = produce(project, (draft) => {
            draft.id = projectId;
            draft.updated_at = new Date().toISOString();
            draft._version = version;

            callback(draft);
          });

          state.optimisticUpdateProjectsById[projectId] = newProject;
        });

        return () => {
          set((state) => {
            if (state.optimisticUpdateProjectsById[projectId]?._version === version) {
              delete state.optimisticUpdateProjectsById[projectId];
            }
          });
        };
      },

      async update() {
        const oldUpdatedAt = get()._updatedAt();
        const oldFoldersById = get().foldersById;
        const oldProjectsById = get().projectsById;

        const { folders, projects } = await Folder.getAll(oldUpdatedAt);

        const newUpdatedAt = maxUpdatedAt(folders, projects);
        const newFoldersById = keyBy(folders, "id");
        const newProjectsById = keyBy(projects, "id");

        if (!oldUpdatedAt) {
          set({ foldersById: newFoldersById, projectsById: newProjectsById, optimisticUpdateFoldersById: {}, optimisticUpdateProjectsById: {} });
        } else if (newUpdatedAt > oldUpdatedAt) {
          set({
            foldersById: Object.assign({}, oldFoldersById, newFoldersById),
            projectsById: Object.assign({}, oldProjectsById, newProjectsById),
          });
        }
      },

      async createFolder(parentId, isSeries = false) {
        const { _pushOptimisticUpdateFolder, update } = get();

        const folderId = createId();
        const pop = _pushOptimisticUpdateFolder(folderId, (folder) => {
          folder.parent_folder_id = parentId;
          folder.title = isSeries ? "New Series" : "New Folder";
          folder.is_pending_series = isSeries;
          folder.last_active = newLastActive();
          folder.deleted = false;
          folder.archived = false;
        });

        try {
          const folder = await Folder.create(parentId, folderId, isSeries);
          await update();
          return folder;
        } catch (error) {
          captureException(error);
          return null;
        } finally {
          pop();
        }
      },

      _getDescendants(folderId) {
        const { foldersById, optimisticUpdateFoldersById, projectsById, optimisticUpdateProjectsById } = get();
        const allFolders = Object.assign({}, foldersById, optimisticUpdateFoldersById);
        const allProjects = Object.assign({}, projectsById, optimisticUpdateProjectsById);

        const childFolderIds = [];
        const childProjectIds = [];

        function collect(parentId) {
          for (const folder of Object.values(allFolders)) {
            if (folder.parent_folder_id === parentId) {
              childFolderIds.push(folder.id);
              collect(folder.id);
            }
          }
          for (const project of Object.values(allProjects)) {
            if (project.folder_id === parentId) {
              childProjectIds.push(project.id);
            }
          }
        }

        collect(folderId);
        return { childFolderIds, childProjectIds };
      },

      async deleteFolder(folderId) {
        const { _pushOptimisticUpdateFolder, _pushOptimisticUpdateProject, _getDescendants, update } = get();
        const { childFolderIds, childProjectIds } = _getDescendants(folderId);

        const pops = [
          _pushOptimisticUpdateFolder(folderId, (folder) => { folder.deleted = true; }),
          ...childFolderIds.map((id) => _pushOptimisticUpdateFolder(id, (folder) => { folder.deleted = true; })),
          ...childProjectIds.map((id) => _pushOptimisticUpdateProject(id, (project) => { project.deleted = true; })),
        ];

        try {
          await Folder.delete(folderId);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pops.forEach((pop) => pop());
        }
      },

      async restoreFolder(folderId) {
        const { _pushOptimisticUpdateFolder, _pushOptimisticUpdateProject, _getDescendants, update } = get();
        const { childFolderIds, childProjectIds } = _getDescendants(folderId);

        const pops = [
          _pushOptimisticUpdateFolder(folderId, (folder) => { folder.deleted = false; }),
          ...childFolderIds.map((id) => _pushOptimisticUpdateFolder(id, (folder) => { folder.deleted = false; })),
          ...childProjectIds.map((id) => _pushOptimisticUpdateProject(id, (project) => { project.deleted = false; })),
        ];

        try {
          await Folder.restore(folderId);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pops.forEach((pop) => pop());
        }
      },

      async moveFolder(folderId, parentId) {
        const { _pushOptimisticUpdateFolder, update } = get();

        const pop = _pushOptimisticUpdateFolder(folderId, (folder) => {
          folder.parent_folder_id = parentId;
          folder.last_active = newLastActive();
        });

        try {
          await Folder.move(folderId, parentId);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async moveProject(projectId, parentId) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(projectId, (project) => {
          project.folder_id = parentId;
          project.last_active = newLastActive();
        });

        try {
          await Folder.moveProject(projectId, parentId);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async setProjectCover(project, payload) {
        const { update } = get();

        await Project.uploadCover(project.firebase_id, payload);
        await update();
      },

      async renameProject(project, title) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (draft) => {
          draft.title = title;
        });

        try {
          const firebaseProject = await Project.get(project.firebase_id);
          await firebaseProject.update({ title, hasAutomaticTitle: false, touch: false });
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async removeProjectCover(project) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (draft) => {
          draft.cover_image_id = null;
          draft.cover_image_url = null;
        });

        try {
          await Project.removeCover(project.firebase_id);
          await update();
        } catch (error) {
          captureException(error);
          throw error;
        } finally {
          pop();
        }
      },

      async deleteProject(project) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (project) => {
          project.deleted = true;
        });

        try {
          await Project.delete(project.firebase_id);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async restoreProject(project) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (project) => {
          project.deleted = false;
        });

        try {
          await Project.restore(project.firebase_id);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async archiveDeleteProject(project) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (project) => {
          project.archived = true;
        });

        try {
          await Project.archiveDelete(project.firebase_id);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async archiveDeleteFolder(folderId) {
        const { _pushOptimisticUpdateFolder, _pushOptimisticUpdateProject, _getDescendants, update } = get();
        const { childFolderIds, childProjectIds } = _getDescendants(folderId);

        const pops = [
          _pushOptimisticUpdateFolder(folderId, (folder) => { folder.archived = true; }),
          ...childFolderIds.map((id) => _pushOptimisticUpdateFolder(id, (folder) => { folder.archived = true; })),
          ...childProjectIds.map((id) => _pushOptimisticUpdateProject(id, (project) => { project.archived = true; })),
        ];

        try {
          await Folder.archiveDelete(folderId);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pops.forEach((pop) => pop());
        }
      },

      async renameFolder(folderId, title) {
        const { _pushOptimisticUpdateFolder, update } = get();

        const pop = _pushOptimisticUpdateFolder(folderId, (folder) => {
          folder.title = title;
        });

        try {
          await Folder.rename(folderId, title);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async duplicateProject(project) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (project) => {
          project.isDuplicating = true;
        });

        try {
          await Project.duplicateProject(project.firebase_id);
          await update();
        } catch (error) {
          captureException(error);
          throw error;
        } finally {
          pop();
        }
      },

      async importProject(title, folderParentId) {
        const { _pushOptimisticUpdateProject, update } = get();

        const projectId = createId();
        const pop = _pushOptimisticUpdateProject(projectId, (project) => {
          project.title = title;
          project.folder_id = folderParentId;
          project.is_importing = true;
          project.last_active = newLastActive();
          project.deleted = false;
          project.archived = false;
          project.words = 0;
        });

        let project;

        try {
          const { project: newProject } = await Auth.fetchWithIdToken("/api/projects/import", { title, source: "import-wip", folderParentId, folderProjectId: projectId });
          project = newProject;
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }

        return project;
      },

      async cancelImport(project, preserveProject = false) {
        const { _pushOptimisticUpdateProject, update } = get();

        const pop = _pushOptimisticUpdateProject(project.id, (project) => {
          project.is_importing = false;
          if (!preserveProject) {
            project.archived = true;
          }
        });

        try {
          await Auth.fetchWithIdToken(`/api/projects/${project.firebase_id}/cancel-import`);
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pop();
        }
      },

      async updateSeriesOrder(updates, folder) {
        const { _pushOptimisticUpdateProject, update } = get();

        // create a map of optimistic updates for each project
        const pops = updates.map((update) =>
          _pushOptimisticUpdateProject(update.id, (project) => {
            project.series_order = update.seriesOrder;
            // don't update last active since the project wasn't opened
          })
        );

        try {
          await Auth.fetchWithIdToken(`/api/series/${folder.series_id}`, {
            method: "PUT",
            contents: updates,
          });
          await update();
        } catch (error) {
          captureException(error);
        } finally {
          pops.forEach((pop) => pop());
        }
      },
    })),
    {
      name: "all-folders-store",
      storage: createJSONStorage(() => createSafeStorage(typeof window !== "undefined" ? window.localStorage : null)),

      // Don't persist optimistic updates
      partialize: (state) => ({ foldersById: state.foldersById, projectsById: state.projectsById }),
    }
  )
);
